<template>
  <div class="about">
    <div><p style="text-align: right;">{{shop}}</p></div>
    <h1>Smart SKU Inventory Group</h1>
    <section>
      <p><strong>A Smart SKU Inventory Group</strong> is a grouping of products or variants defined by the same SKU. The app scans your products and variants; and automatically creates the group when they have the same SKU.</p>
    </section>
    <v-alert style="margin-bottom: 10px" :value="true" color="info" icon="info" outline>
      Need a bit of help?  Contact us: <a href="mailto:support@inscoder.com">support@inscoder.com</a>
    </v-alert>
    <hr v-if="!loading">
    <v-layout v-if="loading" align-center justify-center row fill-height>
      <v-progress-circular
        indeterminate
        color="primary"
      ></v-progress-circular>
    </v-layout>
    <section v-if="!loading">
      <v-layout v-if="autogroup" align-center justify-space-between row>
        <p>This feature is currently turned on. Click the "<strong>Disable</strong>" button to stop using it.</p>
        <p><v-btn large color="primary" v-on:click="disableModeEnabled = true">Disable</v-btn></p>
      </v-layout>
      <v-layout v-else align-center justify-space-between row>
        <p>This feature is currently turned off. Click the "<strong>Enable</strong>" button to begin using it.</p>
        <p><v-btn large color="primary" v-on:click="enableAutogroup">Enable</v-btn></p>
      </v-layout>
    </section>
    <section v-if="!loading && autogroup && !groupView">
      <p v-if="autogroup_refresh">Scanning in progress ....  It will usually take between 10-15 minutes for a full scan. You can close this page and come back later to see the groups that are automatically created.</p>
      <div v-else>
        <v-layout align-center justify-space-between row>
          <p>If you have changed SKU or added products which need to connect. Remember to click "<strong>Re-Scan SKU</strong>" to let us scan your products again.</p>
          <p><v-btn large color="primary" v-on:click="enableAutogroup">Re-Scan SKU</v-btn></p>
        </v-layout>

        <p v-if="errored">Sorry, no data can be retrieved</p>
        <v-card v-if="!errored">
          <v-card-title>
            <b>Groups</b>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              append-icon="search"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
            :headers="headers"
            :items="sku_groups"
            class="elevation-1"
            :rows-per-page-items='[500,1000,{"text":"$vuetify.dataIterator.rowsPerPageAll","value":-1}]'
            :search="search"
          >
           <template v-slot:items="props">
              <td>{{ props.item.name }}</td>
              <td class="text-xs-right">
                <a @click="() => { viewGroup(props.item.gid.domain, props.item.name, props.item.sku, props.item.gid.id) }">Edit Group | Inventory</a>
              </td>
            </template>
          </v-data-table>
        </v-card>

      </div>
    </section>

    <section v-if="autogroup && !autogroup_refresh && groupView">
      <div>

        <v-layout align-center justify-space-between row>
          <h2>{{ groupViewName }}</h2>
          <p>
            <v-btn large color="link" @click="() => {openLink(groupViewEditUrl)}">Edit Inventory</v-btn>
            <v-btn large color="primary" v-on:click="closeGroupView">Back to groups</v-btn>
          </p>
        </v-layout>

        <v-data-table
          v-if="!errored"
          :headers="groupHeaders"
          :items="groupViewProducts"
          class="elevation-1"
        >
          <template slot="items" slot-scope="props">
            <td>
              <a v-if="props.item.variant_link && props.item.variant_link !== null" target="_blank" :href="`${props.item.variant_link}`"><strong>{{ props.item.title }}</strong>&nbsp; {{ props.item.variant_title }}</a>
              <span> | </span>
              <a v-if="props.item.inventory_link && props.item.inventory_link !== null" target="_blank" :href="`${props.item.inventory_link}`">(view inventory history)</a>
            </td>
            <td>{{ groupViewSKU }}</td>
          </template>
        </v-data-table>
      </div>
    </section>

    <v-layout v-if="disableModeEnabled" row justify-center>
      <v-dialog v-model="disableModeEnabled" persistent max-width="390">
        <v-btn slot="activator" color="primary" dark>Open Dialog</v-btn>
        <v-card>
          <v-card-title class="headline">Are you sure?</v-card-title>
          <v-card-text>All the groups will be deleted upon disabled.</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="green darken-1" flat @click.native="disableModeEnabled = false; ">No</v-btn>
            <v-btn color="green darken-1" flat @click.native="disableModeEnabled = false; disableAutogroup()">Yes</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>

  </div>
</template>

<script>
import axios from 'axios'
import {globalStore} from '../main.js'

export default {
  computed: {
    count () {
      return this.$store.state.count
    },
    loading () {
      this.checkStatus
      this.checkInventoryLevels
      return this.checkStatus || this.checkInventoryLevels
    }
  },
  created: function () {

  },
  data () {
    return {
      shop: this.$store.state.shop,
      sku_groups: [],
      autogroup: false,
      autogroup_refresh: false,
      groupView: false,
      groupViewName: '',
      groupViewSKU: '',
      groupViewEditUrl: '',
      groupViewId: 0,
      groupViewProducts: null,
      location: null,
      loadingLocation: true,
      checkStatus: true,
      checkInventoryLevels: true,
      scanning: true,
      errored: false,
      checkStatusInterval: null,
      inventoryLevels: [],
      disableModeEnabled: false,
      search: '',
      headers: [
        {
          text: 'SKU',
          align: 'left',
          sortable: true,
          value: 'name'
        },
        { text: 'Actions', align: 'right', value: 'gid', sortable: false }
      ],
      groupHeaders: [
        {
          text: 'Product / Variant',
          align: 'left',
          sortable: true,
          value: 'title'
        },
        { text: 'SKU', align: 'left', value: 'sku', sortable: false }
      ],
    }
  },
  filters: {
    currencydecimal (value) {
      return value.toFixed(2)
    }
  },
  mounted () {
    axios.defaults.headers.common['Authorization'] =  'Bearer ' + this.$store.getters.code

    let check_status_url = 'https://' + this.$store.getters.engine + '/api/autogroup/status'

    axios
      .get(check_status_url)
      .then(response => {
        this.autogroup = (response.data.autogroup == 'enabled')
        this.autogroup_refresh = (response.data.autogroup_refresh == 'in_process')
        if (this.autogroup && (!this.autogroup_refresh)) {
          this.scanGroups()
        } else
        if (this.autogroup && this.autogroup_refresh) {
          this.checkStatusInterval = setInterval(() => {
            this.checkAutogroupStatus()
          }, 300000)
        }
      })
      .catch(error => {
        this.errored = true
      })
      .finally(() => {
        this.checkStatus = false
      })

    this.doCheckInventoryLevels()

  },
  methods: {

    doCheckInventoryLevels () {
      if (globalStore.inventoryLevels != null) {
        this.inventoryLevels = globalStore.inventoryLevels
        this.checkInventoryLevels = false
      } else {
        let check_inventory_url = 'https://' + this.$store.getters.engine + '/api/location/search'

        this.checkInventoryLevels = true

        this.inventoryLevels = []
        let ILVs = this.inventoryLevels

        axios
          .get(check_inventory_url)
          .then(response => {
            response.data.body.data.locations.edges.forEach(function(e){
              e.node.inventoryLevels.edges.forEach(function(edge){
                let inventoryGID = edge.node.id.split('?')[0].split('/')
                ILVs.push(inventoryGID[inventoryGID.length-1])
              })
            })
            globalStore.inventoryLevels = ILVs
          })
          .catch(error => {
            this.errored = true
            console.log(error)
          })
          .finally(() => {
            this.checkInventoryLevels = false
          })
      }
    },

    disableAutogroup () {
      let url = 'https://' + this.$store.getters.engine + '/api/autogroup/status'

      axios
        .post(url, {"autogroup": "disabled"})
        .then(response => {
          this.autogroup = false
        })
        .catch(error => {

        })
        .finally(() => {

        })
    },

    enableAutogroup () {
      let url = 'https://' + this.$store.getters.engine + '/api/autogroup/refresh'

      axios
        .post(url)
        .then(response => {
          if (response.data.status == 'success') {
            this.autogroup = true
            this.autogroup_refresh = true
            this.checkStatusInterval = setInterval(() => {
              this.checkAutogroupStatus()
            }, 300000)
          }
        })
        .catch(error => {
        })
        .finally(() => {
        })
    },

    checkAutogroupStatus () {
      let url = 'https://' + this.$store.getters.engine + '/api/autogroup/status'

      axios
        .get(url)
        .then(response => {
          this.autogroup = (response.data.autogroup == 'enabled')
          this.autogroup_refresh = (response.data.autogroup_refresh == 'in_process')
          if (this.autogroup && (!this.autogroup_refresh)) {
            clearInterval(this.checkStatusInterval)
            this.scanGroups()
          }
        })
        .catch(error => {
        })
        .finally(() => {
        })
    },

    scanGroups (page = 1) {
      let url = `https://${this.$store.getters.engine}/api/autogroup${page ? `?page=${page}` : ''}`

      if (page == 1) {
        this.sku_groups = []
      }

      axios
        .get(url)
        .then(response => {
          if (response.data && response.data.length > 0) {
            response.data.forEach(g => {
              this.sku_groups.push({
                value: g.id,
                name: g.group_name,
                sku: g.sku,
                gid: {domain: g.shopify_domain, id: g.id}
              })
            })
            this.scanGroups(page + 1)
          }
        })
        .catch(error => {
          this.errored = true
        })
        .finally(() => {})
    },

    viewGroup (domain, name, sku, id) {

      this.groupViewId = id
      this.groupViewName = name
      this.groupViewSKU = sku
      this.groupViewEditUrl = ''
      this.groupViewProducts = []
      this.groupView = true

      let url = 'https://' + this.$store.getters.engine + '/api/autogroup/' + id
      let ILs = this.inventoryLevels

      axios
        .get(url)
        .then(response => {

          let vids = ''

          response.data.forEach(p => {
            this.groupViewProducts.push({
              value: false,
              id: p.id,
              title: p.shopify_product_title,
              variant_title: p.shopify_variant_title,
              inventory_link: `https://${domain}/admin/products/inventory/${p.inventory_item_id}/inventory_history`,
              variant_link: `https://${domain}/admin/products/${p.shopify_product_id}/variants/${p.shopify_variant_id}/`
            })

            vids += (vids == '' ? p.shopify_variant_id : ',' + p.shopify_variant_id)
          })

          if (ILs.length <= -1) {
            this.groupViewEditUrl = `https://${domain}/admin/bulk?resource_name=ProductVariant&edit=inventory_quantity_adjustment,price&show=full_title,title,sku&ids=${vids}`
          } else {
            let inventoryLVs = ''
            ILs.forEach(function(lv) {
              inventoryLVs += ',inventory_levels.' + lv + '.available_adjustment'
            })
            this.groupViewEditUrl = `https://${domain}/admin/bulk?resource_name=ProductVariant&edit=price${inventoryLVs}&show=full_title,title,sku&ids=${vids}`
          }

        })
        .catch(error => {
        })
        .finally(() => {})
    },

    closeGroupView() {
      this.groupView = false
    },

    openLink(url) {
      window.open(url)
    }
  }

}
</script>
